import { Link, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import EmptyState from "@/components/base/EmptyState";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_ICONS,
  AGENCE_INTERVENTION_PRIORITE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_TONES,
  AGENCE_INTERVENTION_STATUT_ICONS,
  AGENCE_INTERVENTION_STATUT_LABELS,
  AGENCE_INTERVENTION_STATUT_OPTIONS,
  AGENCE_INTERVENTION_STATUT_TONES,
} from "@/pages/agence/data/types";
import { formatDate, formatFcfa } from "@/utils/format";

interface AccessModule {
  label: string;
  to: string;
  icon: string;
  hint: string;
}

const ACCESS_MODULES: AccessModule[] = [
  { label: "Immeuble", to: "/agence/immeubles", icon: "ri-building-2-line", hint: "Bâtiment concerné" },
  { label: "Bien", to: "/agence/biens", icon: "ri-home-4-line", hint: "Logement concerné" },
  { label: "Dépenses", to: "/agence/depenses", icon: "ri-money-cny-circle-line", hint: "Charges associées" },
  { label: "Documents", to: "/agence/documents", icon: "ri-folder-line", hint: "Factures, devis" },
];

export default function AgenceInterventionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getIntervention, updateInterventionStatut, getBien, getImmeuble } = useAgenceData();
  const intervention = id ? getIntervention(id) : undefined;

  if (!intervention) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/maintenance"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour à la maintenance
        </Link>
        <EmptyState
          icon="ri-tools-line"
          title="Intervention introuvable"
          description="Cette intervention n'existe pas ou n'est plus disponible. Retournez à la liste pour la retrouver."
          action={
            <Link
              to="/agence/maintenance"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir toutes les interventions
            </Link>
          }
        />
      </div>
    );
  }

  const bien = intervention.bienId ? getBien(intervention.bienId) : undefined;
  const immeuble = intervention.immeubleId ? getImmeuble(intervention.immeubleId) : undefined;
  const emplacement =
    [bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "", immeuble?.nom ?? ""].filter(Boolean).join(" · ") ||
    "Aucun rattachement";

  const infos: { label: string; value: string; icon: string }[] = [
    {
      label: "Priorité",
      value: AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite],
      icon: AGENCE_INTERVENTION_PRIORITE_ICONS[intervention.priorite],
    },
    {
      label: "Statut",
      value: AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut],
      icon: AGENCE_INTERVENTION_STATUT_ICONS[intervention.statut],
    },
    { label: "Immeuble", value: immeuble ? immeuble.nom : "—", icon: "ri-building-2-line" },
    {
      label: "Bien",
      value: bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "—",
      icon: "ri-home-4-line",
    },
    { label: "Prestataire", value: intervention.prestataire || "—", icon: "ri-user-star-line" },
    {
      label: "Coût",
      value: intervention.cout > 0 ? formatFcfa(intervention.cout) : "—",
      icon: "ri-money-cny-circle-line",
    },
    { label: "Date", value: formatDate(intervention.date), icon: "ri-calendar-line" },
    { label: "Ajoutée le", value: formatDate(intervention.createdAt), icon: "ri-time-line" },
  ];

  const scoped: Record<string, string> = {
    Immeuble: immeuble ? `/agence/immeubles/${immeuble.id}` : "/agence/immeubles",
    Bien: bien ? `/agence/biens/${bien.id}` : "/agence/biens",
    Dépenses: immeuble ? `/agence/depenses?immeuble=${immeuble.id}` : "/agence/depenses",
    Documents: bien
      ? `/agence/documents?bien=${bien.id}`
      : immeuble
        ? `/agence/documents?immeuble=${immeuble.id}`
        : "/agence/documents",
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/maintenance"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour à la maintenance
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-accent-50 text-accent-700">
            <i className="ri-tools-line text-2xl" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                tone={AGENCE_INTERVENTION_STATUT_TONES[intervention.statut]}
                icon={AGENCE_INTERVENTION_STATUT_ICONS[intervention.statut]}
              >
                {AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut]}
              </Badge>
              <Badge
                tone={AGENCE_INTERVENTION_PRIORITE_TONES[intervention.priorite]}
                icon={AGENCE_INTERVENTION_PRIORITE_ICONS[intervention.priorite]}
              >
                Priorité {AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite]}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {intervention.probleme}
            </h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-map-pin-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">{emplacement}</span>
            </p>
          </div>
        </div>

        <div className="w-full lg:w-56 lg:shrink-0">
          <label htmlFor="agence-int-detail-statut" className="text-xs font-label font-medium text-foreground-700">
            Changer le statut
          </label>
          <div className="mt-1.5">
            <select
              id="agence-int-detail-statut"
              value={intervention.statut}
              onChange={(event) =>
                updateInterventionStatut(intervention.id, event.target.value as typeof intervention.statut)
              }
              className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/60"
            >
              {AGENCE_INTERVENTION_STATUT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations de l'intervention
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            {intervention.description && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{intervention.description}</p>
              </div>
            )}

            {intervention.photos.length > 0 && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">Photos</p>
                <div className="mt-2 flex flex-wrap gap-3">
                  {intervention.photos.map((photo, index) => (
                    <a
                      key={`${photo.slice(0, 24)}-${index}`}
                      href={photo}
                      target="_blank"
                      rel="noreferrer"
                      className="h-24 w-24 overflow-hidden rounded-md border border-background-200 bg-background-100 cursor-pointer"
                    >
                      <img
                        src={photo}
                        alt={`Photo ${index + 1} de l'intervention`}
                        className="h-24 w-24 object-cover object-top"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {ACCESS_MODULES.map((module) => (
              <Link
                key={module.to}
                to={scoped[module.label] ?? module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}